/**
 * 상품 정보 조사(판매몰 상품) 고르개의 값 — 보드 종류 · 검색 정렬 · 판매 지역 · 상세페이지 태그 축.
 *
 * 🔑 화면(`sections/market`)과 「질문 → 화면」 조건표(`ask-screens`)가 함께 읽는 한 벌이다 — 값은 경계 어휘 그대로.
 */

import type { ProductRankingSort } from 'src/types/product-rankings/product-rankings-scope';

/** 보드 종류 — 검색 결과의 「종류」 좁히기도 같은 값이다 */
export const PRODUCT_KIND_OPTIONS = [
  { value: 'ranking', label: '제품 랭킹' },
  { value: 'new', label: '신상 랭킹' },
];

/**
 * 검색 결과 정렬 — **값은 서버 어휘**(`ProductRankingSort`) 그대로다.
 *
 * ⚠️ 관련도순은 **빈 문자열**이다. 보드의 기본값(`rank`)을 여기 쓰면 안 된다 — 검색의
 * 기본은 `_score`이고 `sort`를 명시했을 때만 바뀐다.
 * ⚠️ `reviews`는 **복수형**이다. 단수 `review`는 서버 `PRODUCT_SORT_MAP`에 없어 조용히
 * 관련도순으로 떨어진다 — 오류도 안 난다.
 * ⚠️ 서버에는 `rating`(별점순)도 있지만 **선택지로 두지 않는다** — 일부러 뺀 것이다.
 */
export const PRODUCT_SEARCH_SORT_OPTIONS: { value: ProductRankingSort | ''; label: string }[] = [
  { value: '', label: '관련도순' },
  { value: 'rank', label: '랭킹 높은순' },
  { value: 'priceAsc', label: '가격 낮은순' },
  { value: 'priceDesc', label: '가격 높은순' },
  { value: 'discount', label: '할인율 높은순' },
  { value: 'reviews', label: '리뷰 많은순' },
];

/**
 * 검색 결과의 「판매 지역」 칩 — 빈 값이 전체.
 *
 * ⚠️ 채널(`판매몰`) 칩과 **다른 축이다.** 해외 채널이 더 붙을 예정이라 이름을 하나씩
 * 고르는 대신 지역으로 묶는다 — 채널이 늘어도 이 목록은 그대로다.
 */
export const PRODUCT_REGION_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'domestic', label: '국내' },
  { value: 'overseas', label: '글로벌' },
];

/**
 * 상세페이지 AI 태그 축 — 검색 패싯 필터와 카드의 태그 클릭이 같은 `param`을 쓴다.
 * ⚠️ 둘이 갈리면 태그를 눌렀을 때 그 태그로 걸리지 않는다.
 */
export const PRODUCT_TAG_FILTER_GROUPS = [
  { param: 'productType', label: '유형', color: 'primary' },
  { param: 'form', label: '제형', color: 'primary' },
  { param: 'concern', label: '고민', color: 'success' },
  { param: 'ingredient', label: '성분', color: 'warning' },
  { param: 'area', label: '부위', color: 'info' },
] as const;

/**
 * 판매몰 실시간 검색을 한 번에 몇 건까지 받나 — 상품 화면과 홈 답 카드가 같은 수로 묻는다.
 *
 * ⚠️ **상한이 50이다** — 우리 BFF 도 백엔드(`POST /api/products/search` · `GET /api/amazon/search`)도
 * 50 에서 자른다. 더 올려 보내도 잘린다.
 *
 * 이 갈래는 「랭킹에 없는 것」을 보충하는 자리인데,
 * 해외 채널은 색인이 이제 막 쌓이기 시작해 **실시간분이 사실상 전부**다. 20 이면 화면이
 * 비어 보인다. 색인 검색(ES)은 원래 60건을 본다 — 그쪽과 자릿수를 맞추는 셈이기도 하다.
 *
 * ⚠️ 요청 수는 안 늘어난다 — 몰당 한 번씩 치는 것은 그대로이고 건수만 는다.
 */
export const LIVE_PRODUCT_SEARCH_LIMIT = 50;
